import type { ErrorObject } from "ajv"
import {
  validatePlan as validatePlanStandalone,
  validateRelease as validateReleaseStandalone,
} from "./generated/application-composition-validators"
import { evaluateCompositionQuality } from "./quality-policy"
import type {
  ApplicationCompositionPlan,
  CompositionInputAdmissionResult,
  DesignCompositionRelease,
  DesignGap,
  DesignGapCode,
  ProjectValidationCompositionAuthority,
} from "./types"

const PROJECT_AUTHORITY_KEYS = [
  "authorityMode",
  "authorityContext",
  "authorityContextHash",
  "applicationCoreRef",
  "designCompositionReleaseRef",
  "designCompositionReleaseHash",
  "sourceMaterializationReceiptRef",
  "sourceMaterializationReceiptHash",
  "contentHash",
] as const
const PROJECT_CONTEXT_KEYS = [
  "admittedUxResearchRefs",
  "approvedCanonicalSourceRefs",
  "admittedApplicationCoreRefs",
  "approvedProjectValidationContractRefs",
  "approvedProjectValidationCoreRefs",
  "aggregationScope",
  "sliceId",
  "contractHash",
  "nonPromotion",
] as const
const PROJECT_CONTRACT_REF =
  "cogo:project-validation-contract/wonbanking-account-selection-v1"
const PROJECT_CORE_REF =
  "cogo:project-validation-application-core/wonbanking-account-selection-v1"
const SHA256_PATTERN = /^sha256:[a-f0-9]{64}$/u

// `validatePlan` and `validateRelease` are PRECOMPILED at build time, because
// Ajv's `.compile()` generates validator code via `new Function(...)` and the
// production Pages CSP does not grant `'unsafe-eval'`. Deferring the compile to
// first call — what this module did before — protected the IMPORT, which is
// what the 2026-07-25 white-screen outage needed, but left the CALL throwing in
// the page. Regenerate with
// `pnpm --filter @cogo/agentic-generator generate:composition-admission-validators`.
// See src/__tests__/no-module-scope-codegen.test.ts.
//
// The generated module is `@ts-nocheck`, so its exports carry no Ajv typing.
// This is the same local shape `packages/cogo-react-renderer/src/document-model/
// document-model-admission.ts` declares for its standalone validator.
type StandaloneValidateFunction = ((value: unknown) => boolean) & {
  errors?: ErrorObject[] | null
}

const validatePlan =
  validatePlanStandalone as unknown as StandaloneValidateFunction
const validateRelease =
  validateReleaseStandalone as unknown as StandaloneValidateFunction

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function evidenceRefs(release: unknown): string[] {
  if (!isRecord(release) || !Array.isArray(release.sourceRefs)) return []
  return release.sourceRefs.filter(
    (candidate): candidate is string => typeof candidate === "string",
  )
}

function jsonPointerPath(
  instancePath: string,
  additionalProperty?: unknown,
): string {
  const segments = instancePath
    .split("/")
    .slice(1)
    .map((segment) =>
      segment.replace(/~1/g, "/").replace(/~0/g, "~"),
    )
  if (typeof additionalProperty === "string") {
    segments.push(additionalProperty)
  }
  return segments
    .map((segment, index) =>
      /^\d+$/.test(segment)
        ? `[${segment}]`
        : index === 0
          ? segment
          : `.${segment}`,
    )
    .join("")
}

function schemaGap(
  error: ErrorObject,
  kind: "plan" | "release",
  release: unknown,
): DesignGap {
  const path = jsonPointerPath(
    error.instancePath,
    error.keyword === "additionalProperties"
      ? error.params.additionalProperty
      : undefined,
  )
  const isRawPresentation =
    kind === "plan" &&
    error.keyword === "additionalProperties" &&
    /semanticNodes\[\d+\]\.content\./.test(path)
  let code: DesignGapCode = isRawPresentation
    ? "raw-presentation-forbidden"
    : kind === "plan"
      ? "plan-schema-invalid"
      : "release-schema-invalid"

  if (kind === "plan" && path === "states") {
    code = "plan-state-invalid"
  } else if (kind === "plan" && path === "semanticNodes") {
    code = "semantic-node-missing"
  } else if (
    kind === "release" &&
    path === "authorityStatus" &&
    error.keyword === "const"
  ) {
    code = "release-not-consumer-verified"
  } else if (
    kind === "plan" &&
    /semanticNodes\[\d+\]\.visibleIn/.test(path)
  ) {
    code = "node-visibility-invalid"
  }

  return {
    code,
    path,
    message: `${kind === "plan" ? "Plan" : "Release"} schema admission failed: ${error.message ?? error.keyword}.`,
    evidenceRefs: [...evidenceRefs(release)],
  }
}

function dedupeGaps(gaps: DesignGap[]): DesignGap[] {
  const seen = new Set<string>()
  return gaps.filter((candidate) => {
    const key = `${candidate.code}\u0000${candidate.path}\u0000${candidate.message}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/**
 * The single public runtime admission path for composition compilation.
 * Both values are unknown until their canonical JSON schemas and deterministic
 * cross-field policy accept cloned copies.
 */
export function admitApplicationCompositionInputs(
  candidatePlan: unknown,
  candidateRelease: unknown,
  candidateProjectValidationAuthority?: unknown,
): CompositionInputAdmissionResult {
  const planValid = validatePlan(candidatePlan)
  const planErrors = planValid ? [] : [...(validatePlan.errors ?? [])]
  const releaseValid = validateRelease(candidateRelease)
  const releaseErrors = releaseValid
    ? []
    : [...(validateRelease.errors ?? [])]

  const planGaps = planErrors.map((error) =>
    schemaGap(error, "plan", candidateRelease),
  )
  const mappedReleaseGaps = releaseErrors.map((error) =>
    schemaGap(error, "release", candidateRelease),
  )
  // Preserve the pre-project public failure contract: a candidate or retired
  // status is one typed authority gap, not the implementation detail emitted
  // by each branch of the schema's closed oneOf.
  const releaseGaps = mappedReleaseGaps.some(
    (candidate) => candidate.code === "release-not-consumer-verified",
  )
    ? mappedReleaseGaps.filter(
        (candidate) => candidate.code === "release-not-consumer-verified",
      ).slice(0, 1)
    : mappedReleaseGaps
  const schemaGaps = [...planGaps, ...releaseGaps]

  if (!planValid || !releaseValid) {
    return { ok: false, gaps: dedupeGaps(schemaGaps) }
  }

  const plan = structuredClone(
    candidatePlan,
  ) as unknown as ApplicationCompositionPlan
  const release = structuredClone(
    candidateRelease,
  ) as unknown as DesignCompositionRelease
  const planUsesProjectCore = plan.applicationCoreRef.startsWith(
    "cogo:project-validation-application-core/",
  )
  if (
    planUsesProjectCore !==
    (candidateProjectValidationAuthority !== undefined)
  ) {
    return invalidProjectAuthority(release)
  }
  const projectValidationAuthority =
    candidateProjectValidationAuthority === undefined
      ? undefined
      : admitProjectValidationAuthority(
          candidateProjectValidationAuthority,
          plan,
          release,
        )
  if (
    candidateProjectValidationAuthority !== undefined &&
    !projectValidationAuthority
  ) {
    return invalidProjectAuthority(release)
  }
  const policyRelease = projectValidationAuthority
    ? (() => {
        const {
          projectValidationBinding: _projectValidationBinding,
          ...commonRelease
        } = release
        return {
          ...commonRelease,
          authorityStatus: "consumer-verified" as const,
        }
      })()
    : release
  const policyGaps = evaluateCompositionQuality(plan, policyRelease)
  if (policyGaps.length > 0) {
    return { ok: false, gaps: policyGaps }
  }

  return {
    ok: true,
    input: {
      plan,
      release,
      ...(projectValidationAuthority ? { projectValidationAuthority } : {}),
    },
  }
}

function invalidProjectAuthority(
  release: DesignCompositionRelease,
): CompositionInputAdmissionResult {
  return {
    ok: false,
    gaps: [
      {
        code: "project-validation-authority-invalid",
        path: "projectValidationAuthority",
        message:
          "Project-validation composition authority must match its exact closed context, release, and self-hashes.",
        evidenceRefs: [...release.sourceRefs],
      },
    ],
  }
}

function admitProjectValidationAuthority(
  candidate: unknown,
  plan: ApplicationCompositionPlan,
  release: DesignCompositionRelease,
): ProjectValidationCompositionAuthority | null {
  try {
    const admitted = admitProjectValidationAuthorityEnvelope(candidate)
    if (!admitted) return null
    const normalizedSnapshot = admitted as unknown as Readonly<
      Record<string, unknown>
    >
    const context = admitted.authorityContext as unknown as Readonly<
      Record<string, unknown>
    >
    if (
      plan.applicationCoreRef !== PROJECT_CORE_REF ||
      release.authorityStatus !== "project-validation" ||
      !release.projectValidationBinding
    ) {
      return null
    }
    const binding = release.projectValidationBinding
    const sourceAuthority = [...release.sourceRefs].sort()
    const exactSourceAuthority = [
      PROJECT_CONTRACT_REF,
      binding.sourceMaterializationReceiptRef,
    ].sort()
    if (
      binding.aggregationScope !== context.aggregationScope ||
      binding.sliceId !== context.sliceId ||
      binding.contractRef !== PROJECT_CONTRACT_REF ||
      binding.contractHash !== context.contractHash ||
      binding.processLocal !== true ||
      binding.persistenceAllowed !== false ||
      binding.promotionAllowed !== false ||
      sourceAuthority.length !== exactSourceAuthority.length ||
      sourceAuthority.some((ref, index) => ref !== exactSourceAuthority[index]) ||
      normalizedSnapshot.designCompositionReleaseRef !== release.releaseId ||
      normalizedSnapshot.sourceMaterializationReceiptRef !==
        binding.sourceMaterializationReceiptRef ||
      normalizedSnapshot.sourceMaterializationReceiptHash !==
        binding.sourceMaterializationReceiptHash ||
      !isSha256(normalizedSnapshot.designCompositionReleaseHash)
    ) {
      return null
    }
    if (
      normalizedSnapshot.designCompositionReleaseHash !==
        hashProjectValidationAuthorityValue(release)
    ) {
      return null
    }
    return admitted
  } catch {
    return null
  }
}

export function admitProjectValidationAuthorityEnvelope(
  candidate: unknown,
): ProjectValidationCompositionAuthority | null {
  try {
    const snapshot = snapshotClosedRecord(candidate, PROJECT_AUTHORITY_KEYS)
    if (!snapshot) return null
    const context = snapshotClosedRecord(
      snapshot.authorityContext,
      PROJECT_CONTEXT_KEYS,
    )
    if (!context) return null
    const normalizedSnapshot: Readonly<Record<string, unknown>> = Object.freeze({
      ...snapshot,
      authorityContext: context,
    })
    const emptyArray = (value: unknown) =>
      Array.isArray(value) && value.length === 0
    const singleton = (value: unknown, expected: string) =>
      Array.isArray(value) && value.length === 1 && value[0] === expected
    if (
      normalizedSnapshot.authorityMode !== "project-validation" ||
      !emptyArray(context.admittedUxResearchRefs) ||
      !emptyArray(context.approvedCanonicalSourceRefs) ||
      !emptyArray(context.admittedApplicationCoreRefs) ||
      !singleton(
        context.approvedProjectValidationContractRefs,
        PROJECT_CONTRACT_REF,
      ) ||
      !singleton(context.approvedProjectValidationCoreRefs, PROJECT_CORE_REF) ||
      context.aggregationScope !== "wonbanking-library-validation" ||
      context.sliceId !== "account-selection-bottom-sheet" ||
      !isSha256(context.contractHash) ||
      context.nonPromotion !== true ||
      normalizedSnapshot.applicationCoreRef !== PROJECT_CORE_REF ||
      typeof normalizedSnapshot.designCompositionReleaseRef !== "string" ||
      normalizedSnapshot.designCompositionReleaseRef.length === 0 ||
      typeof normalizedSnapshot.sourceMaterializationReceiptRef !== "string" ||
      normalizedSnapshot.sourceMaterializationReceiptRef.length === 0 ||
      !isSha256(normalizedSnapshot.authorityContextHash) ||
      !isSha256(normalizedSnapshot.designCompositionReleaseHash) ||
      !isSha256(normalizedSnapshot.sourceMaterializationReceiptHash) ||
      !isSha256(normalizedSnapshot.contentHash)
    ) {
      return null
    }
    if (
      normalizedSnapshot.authorityContextHash !==
      hashProjectValidationAuthorityValue(context)
    ) {
      return null
    }
    const { contentHash: _contentHash, ...body } = normalizedSnapshot
    if (
      normalizedSnapshot.contentHash !== hashProjectValidationAuthorityValue(body)
    ) {
      return null
    }
    return structuredClone(
      normalizedSnapshot,
    ) as unknown as ProjectValidationCompositionAuthority
  } catch {
    return null
  }
}

function snapshotClosedRecord(
  candidate: unknown,
  expectedKeys: readonly string[],
): Record<string, unknown> | null {
  if (!isRecord(candidate)) return null
  const prototype = Reflect.getPrototypeOf(candidate)
  if (prototype !== Object.prototype && prototype !== null) return null
  const keys = Reflect.ownKeys(candidate)
  if (
    keys.length !== expectedKeys.length ||
    keys.some(
      (key) =>
        typeof key !== "string" || !expectedKeys.includes(key),
    )
  ) {
    return null
  }
  const snapshot: Record<string, unknown> = Object.create(null)
  for (const key of expectedKeys) {
    const descriptor = Reflect.getOwnPropertyDescriptor(candidate, key)
    if (!descriptor || !("value" in descriptor) || !descriptor.enumerable) {
      return null
    }
    const value = descriptor.value
    if (Array.isArray(value)) {
      const arraySnapshot = snapshotDenseStringArray(value)
      if (!arraySnapshot) return null
      snapshot[key] = arraySnapshot
    } else {
      snapshot[key] = value
    }
  }
  return Object.freeze(snapshot)
}

function snapshotDenseStringArray(value: unknown[]): readonly string[] | null {
  if (Reflect.getPrototypeOf(value) !== Array.prototype) return null
  const keys = Reflect.ownKeys(value)
  if (keys.some((key) => typeof key !== "string")) return null
  const lengthDescriptor = Reflect.getOwnPropertyDescriptor(value, "length")
  if (
    !lengthDescriptor ||
    !("value" in lengthDescriptor) ||
    typeof lengthDescriptor.value !== "number" ||
    !Number.isSafeInteger(lengthDescriptor.value) ||
    lengthDescriptor.value < 0 ||
    lengthDescriptor.value > 16
  ) {
    return null
  }
  const length = lengthDescriptor.value
  if (
    keys.length !== length + 1 ||
    !keys.includes("length") ||
    Array.from({ length }, (_, index) => String(index)).some(
      (key) => !keys.includes(key),
    )
  ) {
    return null
  }
  const snapshot: string[] = []
  for (let index = 0; index < length; index += 1) {
    const descriptor = Reflect.getOwnPropertyDescriptor(value, String(index))
    if (
      !descriptor ||
      !("value" in descriptor) ||
      !descriptor.enumerable ||
      typeof descriptor.value !== "string"
    ) {
      return null
    }
    snapshot.push(descriptor.value)
  }
  return Object.freeze(snapshot)
}

function isSha256(value: unknown): value is `sha256:${string}` {
  return typeof value === "string" && SHA256_PATTERN.test(value)
}

/**
 * Browser-safe synchronous SHA-256 over recursively key-sorted JSON. The
 * project authority option is intentionally tiny and bounded by exact keys,
 * so synchronous verification cannot become an attacker-controlled workload.
 */
export function hashProjectValidationAuthorityValue(
  value: unknown,
): `sha256:${string}` {
  const canonical = JSON.stringify(canonicalizeHashValue(value))
  return `sha256:${sha256Hex(canonical)}`
}

function canonicalizeHashValue(value: unknown): unknown {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "boolean" ||
    (typeof value === "number" && Number.isFinite(value))
  ) {
    return value
  }
  if (Array.isArray(value)) return value.map(canonicalizeHashValue)
  if (!isRecord(value)) {
    throw new TypeError("Project authority hash input must be JSON-compatible")
  }
  return Object.fromEntries(
    Object.keys(value)
      .sort()
      .map((key) => [key, canonicalizeHashValue(value[key])]),
  )
}

function sha256Hex(text: string): string {
  const bytes = new TextEncoder().encode(text)
  const bitLength = bytes.length * 8
  const paddedLength = Math.ceil((bytes.length + 9) / 64) * 64
  const padded = new Uint8Array(paddedLength)
  padded.set(bytes)
  padded[bytes.length] = 0x80
  const view = new DataView(padded.buffer)
  view.setUint32(paddedLength - 8, Math.floor(bitLength / 0x1_0000_0000))
  view.setUint32(paddedLength - 4, bitLength >>> 0)
  const h = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
    0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ])
  const k = new Uint32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5,
    0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
    0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc,
    0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7,
    0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
    0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3,
    0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5,
    0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
    0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ])
  const words = new Uint32Array(64)
  const rotateRight = (value: number, bits: number) =>
    (value >>> bits) | (value << (32 - bits))
  for (let offset = 0; offset < paddedLength; offset += 64) {
    for (let index = 0; index < 16; index += 1) {
      words[index] = view.getUint32(offset + index * 4)
    }
    for (let index = 16; index < 64; index += 1) {
      const w15 = words[index - 15]
      const w2 = words[index - 2]
      const s0 = rotateRight(w15, 7) ^ rotateRight(w15, 18) ^ (w15 >>> 3)
      const s1 = rotateRight(w2, 17) ^ rotateRight(w2, 19) ^ (w2 >>> 10)
      words[index] =
        (words[index - 16] + s0 + words[index - 7] + s1) >>> 0
    }
    let [a, b, c, d, e, f, g, hh] = h
    for (let index = 0; index < 64; index += 1) {
      const s1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25)
      const choice = (e & f) ^ (~e & g)
      const temp1 = (hh + s1 + choice + k[index] + words[index]) >>> 0
      const s0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22)
      const majority = (a & b) ^ (a & c) ^ (b & c)
      const temp2 = (s0 + majority) >>> 0
      hh = g
      g = f
      f = e
      e = (d + temp1) >>> 0
      d = c
      c = b
      b = a
      a = (temp1 + temp2) >>> 0
    }
    h[0] = (h[0] + a) >>> 0
    h[1] = (h[1] + b) >>> 0
    h[2] = (h[2] + c) >>> 0
    h[3] = (h[3] + d) >>> 0
    h[4] = (h[4] + e) >>> 0
    h[5] = (h[5] + f) >>> 0
    h[6] = (h[6] + g) >>> 0
    h[7] = (h[7] + hh) >>> 0
  }
  return [...h].map((word) => word.toString(16).padStart(8, "0")).join("")
}
